import React, { useContext, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { EntryContext } from '../contexts/entryContext';
import TeleScript from '../utils/TeleScript';

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrAfter);

const BirthDateSelector = ({ setDobVerified }) => {
    const { siteAssets } = useContext(EntryContext);
    const [birthDate, setBirthDate] = useState(null);
    const [isUnderage, setIsUnderage] = useState(false);

    const minAgeLimit = dayjs().subtract(150, 'year').startOf('day'); // Define 150-year limit
    const eighteenYearsAgo = dayjs().subtract(siteAssets?.birthDateSelection?.DobAgeLimit ?? 18, 'year').startOf('day');

    // Optional custom calendar picker: SVG text format needed for instant load without additional requests
    const calendarIconData = siteAssets?.birthDateSelection?.DobCalendarIcon ?? null;
    const CustomPickerIcon = () => <div id='dob-custom-picker-icon' dangerouslySetInnerHTML={{ __html: calendarIconData }} />;

    const handleDateChange = (newValue) => {
        setBirthDate(newValue);

        // Check if date value is totally invalid or empty, explicitly reject immediately
        if (!newValue || !newValue.isValid()) {
            setIsUnderage(false);
            if (setDobVerified) setDobVerified(false);
            return;
        }

        // Use inclusionary check (.isSameOrAfter) for the 150 year edge case
        if (newValue.isSameOrAfter(minAgeLimit, 'day') && newValue.isBefore(dayjs().endOf('day'))) {
            const underage = newValue.isAfter(eighteenYearsAgo);
            setIsUnderage(underage);

            if (setDobVerified) {
                setDobVerified(!underage);
            }
        } else {
            // Older than 150 years or in the future: invalid
            setIsUnderage(false);
            if (setDobVerified) setDobVerified(false);
        }
    };

    return (
        <div id='age-verification-birth-date-selector-container' className='birth-date-selector-container'>
            <div id='age-verification-birth-date-selector' className='birth-date-selector'>
                {isUnderage && (
                    <div className='age-error-banner'>
                        <TeleScript line={siteAssets?.birthDateSelection?.DobAlertMessage}>You must meet the age requirement.</TeleScript>
                    </div>
                )}

                <div className='birth-date-input-wrapper'>
                    <span className='birth-date-label'>
                        <TeleScript line={siteAssets?.birthDateSelection?.DobLabelText}>Date of Birth</TeleScript>
                    </span>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={birthDate}
                            onChange={handleDateChange}
                            minDate={minAgeLimit} // Disables dates older than 150 years in the calendar
                            maxDate={dayjs().endOf('day')} // Disables future dates in the calendar
                            slots={{
                                ...(calendarIconData && { openPickerIcon: CustomPickerIcon }), // Custom calendar picker icon
                            }}
                            slotProps={{
                                toolbar: {
                                    component: 'h2',
                                    role: 'heading',
                                    'aria-level': 2,
                                },
                                actionBar: {
                                    actions: ['clear', 'cancel', 'accept'],
                                    // Add the 'auto' margin to push 'Clear' to the left and 'Cancel' and 'OK' to the right
                                    sx: {
                                        '& > button:first-of-type': {
                                            marginRight: 'auto',
                                        },
                                    },
                                },
                                textField: {
                                    className: `dob-textfield ${isUnderage ? 'dob-error' : ''}`,
                                    placeholder: 'MM/DD/YYYY',
                                    error: isUnderage, // This toggles the .Mui-error class for our CSS
                                    InputProps: {
                                        classes: {
                                            root: `dob-input-root ${isUnderage ? 'dob-error' : ''}`,
                                            notchedOutline: `dob-notched-outline ${isUnderage ? 'dob-error' : ''}`,
                                            input: `dob-input ${isUnderage ? 'dob-error' : ''}`,
                                        },
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </div>
            </div>
        </div>
    );
};

export default BirthDateSelector;
